<template>
  <div class="automated-assessment-page">
    <div class="assessment-header">
      <h2 class="assessment-title">
        Automated Assessment
      </h2>
    </div>

    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadInitialData">Retry</button>
      </div>

      <div v-else class="requirements-state">
        <div class="req-dashboard-header">
          <i :class="requiredSetupStepsCompleted === requiredSetupStepsTotal ? 'las la-check-circle req-icon-ok' : 'las la-clipboard-list'"></i>
          <div>
            <h3>Assessment Setup Guide</h3>
            <p>
              {{ requiredSetupStepsCompleted }} of {{ requiredSetupStepsTotal }} required steps complete.
              Follow the steps below to run and analyse the credit model.
            </p>
          </div>
        </div>

        <ol class="setup-guide">
          <li
            v-for="step in setupGuideSteps"
            :key="step.key"
            class="setup-guide-step"
            :class="{ met: step.met, locked: step.locked, optional: step.optional }"
          >
            <div class="setup-guide-number">
              <i v-if="step.met" class="las la-check"></i>
              <span v-else>{{ step.number }}</span>
            </div>
            <div class="setup-guide-body">
              <div class="setup-guide-title-row">
                <span class="setup-guide-title">{{ step.title }}</span>
                <span v-if="step.optional" class="req-field-optional">optional</span>
                <span v-else-if="step.locked" class="setup-guide-locked-label">locked</span>
              </div>
              <p class="setup-guide-description">{{ step.description }}</p>
            </div>
            <div class="setup-guide-action">
              <BaseButton
                stylevariant="primary"
                :cus_style="{ width: '100%', padding: '0.4rem 0.75rem', fontSize: '0.8rem' }"
                :disabled="step.locked || (step.key === 'run' && !requirementsMet)"
                :loading="step.key === 'run' && uploading"
                @click="step.action()"
              >
                {{ step.actionLabel }}
              </BaseButton>
            </div>
          </li>
        </ol>

        <div class="req-sections">
          <!-- Documents — featured full-width -->
          <div
            v-for="section in requirementSections.filter(s => s.key === 'documents')"
            :key="section.key"
            class="req-section req-section-documents"
          >
            <div class="req-section-head">
              <i :class="section.icon"></i>
              <span>{{ section.title }}</span>
              <span
                class="req-section-badge"
                :class="section.fields.filter(f => !f.optional).every(f => f.met) ? 'complete' : 'incomplete'"
              >
                {{ section.fields.filter(f => !f.optional && f.met).length }} / {{ section.fields.filter(f => !f.optional).length }}
              </span>
            </div>

            <div class="req-doc-grid">
              <div
                v-for="field in section.fields"
                :key="field.key"
                class="req-doc-card"
                :class="{ met: field.met, optional: field.optional }"
              >
                <div class="req-doc-card-top">
                  <i :class="field.met ? 'las la-check' : 'las la-times'" class="req-doc-status"></i>
                  <div class="req-doc-card-info">
                    <span class="req-doc-label">{{ field.label }}</span>
                    <span v-if="field.optional && !field.met" class="req-field-optional">optional</span>
                    <span v-else-if="field.met" class="req-doc-value">{{ field.displayValue }}</span>
                    <span v-else class="req-doc-missing">Not uploaded</span>
                  </div>
                </div>
                <BaseButton
                  stylevariant="primary"
                  :cus_style="{ width: 'auto', padding: '0.4rem 0.875rem', fontSize: '0.8rem' }"
                  @click="$router.push(field.route)"
                >
                  {{ field.met ? 'View' : 'Upload' }}
                </BaseButton>
              </div>
            </div>
          </div>

          <!-- Org + Facility -->
          <div
            v-for="section in requirementSections.filter(s => s.key !== 'documents')"
            :key="section.key"
            class="req-section"
          >
            <div class="req-section-head">
              <i :class="section.icon"></i>
              <span>{{ section.title }}</span>
              <span
                class="req-section-badge"
                :class="section.fields.filter(f => !f.optional).every(f => f.met) ? 'complete' : 'incomplete'"
              >
                {{ section.fields.filter(f => !f.optional && f.met).length }} / {{ section.fields.filter(f => !f.optional).length }}
              </span>
            </div>

            <ul class="req-field-list">
              <li
                v-for="field in section.fields"
                :key="field.key"
                class="req-field-item"
                :class="{ met: field.met }"
              >
                <i :class="field.met ? 'las la-check' : 'las la-times'"></i>
                <span class="req-field-label">{{ field.label }}</span>
                <span v-if="field.optional && !field.met" class="req-field-optional">optional</span>
                <span v-if="field.met" class="req-field-value">{{ field.displayValue }}</span>
              </li>
            </ul>

            <div class="req-section-action">
              <BaseButton
                v-if="section.key === 'org'"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.375rem 0.75rem', fontSize: '0.8rem' }"
                @click="goToOrganisationDetails"
              >
                {{ section.fields.every(f => f.met) ? 'Edit Org Details' : 'Go to Organisation Details' }}
              </BaseButton>

              <BaseButton
                v-else-if="section.key === 'facility'"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.375rem 0.75rem', fontSize: '0.8rem' }"
                @click="goToOrganisationDetails"
              >
                {{ section.fields.every(f => f.met) ? 'Edit Facility Details' : 'Go to Facility Details' }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>

      <div v-if="requirementsMet" class="assessment-ready">
        <div ref="runModelSection" class="org-details-section">

          <div v-if="organisationDebts.length > 0" class="debts-section">
            <h3 class="subsection-title">Debts</h3>
            <table class="debts-table">
              <thead>
                <tr>
                  <th>Lender</th>
                  <th>Approval Amount</th>
                  <th>Amount Outstanding</th>
                  <th>Repayment/Interest Service</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(debt, index) in organisationDebts" :key="index">
                  <td>{{ debt.lender || '—' }}</td>
                  <td>{{ formatDebtAmount(debt.approval_amount) }}</td>
                  <td>{{ formatDebtAmount(debt.amount_outstanding) }}</td>
                  <td>{{ formatDebtAmount(debt.repayment_interest_service) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="creditModelDoc" class="credit-model-section">
            <h3 class="subsection-title">Latest Credit Model Result</h3>
            <div class="document-card">
              <div class="document-card-content">
                <div class="document-icon-large">
                  <i class="las la-file-excel"></i>
                </div>
                <span class="document-name">{{ creditModelDoc.name }}</span>
                <span class="document-date">
                  {{ formatUploadDate(creditModelDoc.uploaded_at) }}
                </span>
                <span class="status-badge" :class="getDocumentStatusClass(creditModelDoc.status)">
                  <i :class="getDocumentStatusIcon(creditModelDoc.status)"></i>
                </span>
                <button
                  v-if="creditModelDoc.status === 'completed' || creditModelDoc.status === 'success'"
                  type="button"
                  class="view-btn"
                  @click.stop="viewCreditModelDocument"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <button
                  v-if="creditModelDoc.status === 'completed' || creditModelDoc.status === 'success'"
                  type="button"
                  class="view-btn"
                  :disabled="downloadingCreditModelDoc"
                  @click.stop="downloadCreditModelDocument"
                  title="Download Document"
                >
                  <i :class="downloadingCreditModelDoc ? 'las la-spinner la-spin' : 'las la-file-download'"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="uploading || uploadProgress !== null" class="progress-section">
            <div class="progress-info">
              <div class="progress-status" :class="uploadStatus">
                <i :class="getStatusIcon(uploadStatus)"></i>
                <span>{{ uploadStatusText }}</span>
              </div>
              <div v-if="uploadProgress !== null && uploadProgress !== undefined" class="progress-bar-container">
                <div class="progress-bar">
                  <div
                    class="progress-bar-fill"
                    :style="{ width: `${uploadProgress}%` }"
                  ></div>
                </div>
                <div class="progress-percentage">{{ uploadProgress }}%</div>
              </div>
              <div v-if="uploadError" class="progress-error">
                <i class="las la-exclamation-triangle"></i>
                <span>{{ uploadError }}</span>
              </div>
            </div>
          </div>

          <div class="run-model-credit-limits" v-if="showCreditLimitsPanel">
            <div v-if="creditLimitsLoading" class="run-model-credit-limits-loading">
              Loading credit limits...
            </div>
            <div v-else-if="creditLimitsError" class="credit-limits-error">
              <i class="las la-exclamation-triangle"></i>
              <div>
                <p>{{ creditLimitsError }}</p>
                <button type="button" class="retry-btn" @click="loadCreditLimits">Retry</button>
              </div>
            </div>
            <template v-else-if="hasCreditLimits">
              <div
                v-if="creditLimits.message"
                class="credit-limits-message"
                :class="creditLimitsStatusClass"
              >
                <i :class="creditLimitsStatusIcon"></i>
                <p>{{ creditLimits.message }}</p>
              </div>

              <div class="run-model-credit-limits-grid" :class="{ 'has-wcr': showWcrSummary }">
                <div class="run-model-credit-limit">
                  <span class="run-model-credit-label">Requested Credit Limit</span>
                  <strong class="run-model-credit-value">{{ formatCreditLimit(creditLimits.requested_credit_limit) }}</strong>
                </div>
                <div class="run-model-credit-limit">
                  <span class="run-model-credit-label">
                    Approved Credit Limit
                    <span class="run-model-credit-subtext">WCR @ 1.2</span>
                  </span>
                  <strong class="run-model-credit-value approved">{{ formatCreditLimit(creditLimits.approved_credit_limit) }}</strong>
                </div>
                <div v-if="showWcrSummary" class="run-model-credit-limit">
                  <span class="run-model-credit-label">
                    Adjusted WCR
                    <span class="run-model-credit-subtext">to match requested credit limit</span>
                  </span>
                  <strong class="run-model-credit-value">{{ formatWcr(creditLimits.wcr) }}</strong>
                </div>
              </div>

              <div v-if="showCreditLimitOptions || creditLimitsStatus === 'reject'" class="credit-limit-options">
                <p class="credit-limit-options-title">{{ creditLimitOptionsTitle }}</p>
                <button
                  v-for="option in creditLimitOptions"
                  :key="option.id"
                  type="button"
                  class="credit-limit-option-card"
                  :class="{ selected: isCreditLimitOptionSelected(option) }"
                  @click="selectCreditLimitOption(option)"
                >
                  <span class="credit-limit-option-radio"></span>
                  <span class="credit-limit-option-body">
                    <span class="credit-limit-option-label">{{ getCreditLimitOptionLabel(option) }}</span>
                    <span class="credit-limit-option-meta">
                      <span>WCR <strong>{{ formatWcr(option.wcr) }}</strong></span>
                      <span>Credit limit <strong>{{ formatCreditLimit(option.credit_limit) }}</strong></span>
                    </span>
                  </span>
                </button>
                <button
                  v-if="creditLimitsStatus === 'reject'"
                  type="button"
                  class="credit-limit-option-card credit-limit-option-card--edit"
                  @click="goToRequestedAmount"
                >
                  <span class="credit-limit-option-body">
                    <span class="credit-limit-option-label">Edit requested amount</span>
                    <span class="credit-limit-option-meta">
                      Change the requested credit limit on Overview
                    </span>
                  </span>
                </button>
              </div>

              <div class="action-buttons">
                <BaseButton
                  v-if="creditLimitsStatus === 'run_immediately'"
                  stylevariant="primary"
                  :disabled="!canRunModel"
                  :loading="uploading"
                  @click="sendToModel"
                >
                  Run Assessment
                </BaseButton>
                <BaseButton
                  v-else-if="showCreditLimitOptions"
                  stylevariant="primary"
                  :disabled="!canContinueWithOption"
                  :loading="uploading || savingCreditLimitOption"
                  @click="continueWithSelectedOption"
                >
                  Continue with selected option
                </BaseButton>
              </div>
            </template>
          </div>

          <div class="action-buttons" v-else>
            <BaseButton
              stylevariant="primary"
              :disabled="!canRunModel"
              :loading="uploading"
              @click="sendToModel"
            >
              Run Model
            </BaseButton>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { read, utils, write } from 'xlsx';
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';

const ASSESSMENT_STEP_LABELS = {
  payer_scores: 'Formatting payer scores',
  key_strengths: 'Analysing key strengths',
  key_weaknesses: 'Analysing key weaknesses',
  revenue_trends: 'Analysing revenue trends',
  cash_quick_ratio: 'Analysing cash and quick ratio',
  relative_risk_strengths: 'Analysing relative risk strengths',
  relative_risk_weaknesses: 'Analysing relative risk weaknesses',
  conclusion_points: 'Generating conclusion points',
  recommendation_points: 'Generating recommendations',
  completed: 'Analysis completed'
};

export default {
  name: 'AutomatedAssessmentInput',
  components: { BaseButton, BaseSpinner },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      organisationDetails: {},
      erpExtractDoc: null,
      creditModelDoc: null,
      creditModelHasAnalysisData: false,
      candidate: null,
      uploading: false,
      downloadingCreditModelDoc: false,
      uploadProgress: null,
      uploadStatus: null,
      uploadError: null,
      payload: null,
      vatDocuments: [],
      bankStatementDocuments: [],
      kybDocuments: [],
      analysisPackDoc: null,
      primePayersImportFile: null,
      primePayersImporting: false,
      primePayersDeleting: false,
      primePayersChecking: false,
      primePayersCheckResult: null,
      primePayersCheckError: null,
      primePayersCheckFilter: 'all',
      approvedAdvanceRate: '',
      savingApprovedCreditFields: false,
      generatingCreditAssessmentReport: false,
      assessmentWebsocket: null,
      assessmentWebSocketReady: false,
      assessmentProgress: null,
      assessmentError: null,
      creditLimits: null,
      creditLimitsLoading: false,
      creditLimitsError: null,
      selectedCreditLimitOption: null,
      savingCreditLimitOption: false
    };
  },
  computed: {
    hasCreditLimits() {
      if (!this.creditLimits || typeof this.creditLimits !== 'object') {
        return false;
      }

      const { approved_credit_limit: approved, requested_credit_limit: requested, status } = this.creditLimits;
      return (approved !== null && approved !== undefined && approved !== '')
        || (requested !== null && requested !== undefined && requested !== '')
        || !!status;
    },
    showCreditLimitsPanel() {
      return this.creditLimitsLoading || !!this.creditLimitsError || this.hasCreditLimits;
    },
    creditLimitsStatus() {
      return this.creditLimits?.status || null;
    },
    creditLimitsStatusClass() {
      if (this.creditLimitsStatus === 'run_immediately') return 'success';
      if (this.creditLimitsStatus === 'choose_option') return 'warning';
      if (this.creditLimitsStatus === 'reject') return 'error';
      return '';
    },
    creditLimitsStatusIcon() {
      if (this.creditLimitsStatus === 'run_immediately') return 'las la-check';
      if (this.creditLimitsStatus === 'choose_option') return 'las la-exclamation-circle';
      if (this.creditLimitsStatus === 'reject') return 'las la-times-circle';
      return 'las la-info-circle';
    },
    showWcrSummary() {
      const wcr = this.creditLimits?.wcr;
      return wcr !== null && wcr !== undefined && wcr !== '';
    },
    creditLimitOptions() {
      return Array.isArray(this.creditLimits?.options) ? this.creditLimits.options : [];
    },
    showCreditLimitOptions() {
      return this.creditLimitOptions.length > 0
        && (this.creditLimitsStatus === 'choose_option' || this.creditLimitsStatus === 'reject');
    },
    creditLimitOptionsTitle() {
      if (this.creditLimitsStatus === 'reject') {
        return 'Choose an approved limit to proceed';
      }
      return 'Choose how to proceed';
    },
    canContinueWithOption() {
      return this.showCreditLimitOptions
        && !!this.selectedCreditLimitOption
        && this.requirementsMet
        && !this.uploading
        && !this.savingCreditLimitOption;
    },
    creditModelFileId() {
      return this.creditModelDoc?.id || this.creditModelDoc?.file_id || null;
    },
    approvedRate() {
      const rate = this.organisationDetails?.approved_rate ?? this.organisationDetails?.approvedRate;
      return rate === '' || rate === null || rate === undefined ? null : rate;
    },
    approvedRateDisplay() {
      if (this.approvedRate === null) {
        return null;
      }

      const numeric = Number(this.approvedRate);
      if (!Number.isFinite(numeric)) {
        return String(this.approvedRate);
      }

      return `${numeric}%`;
    },
    hasApprovedCreditFields() {
      const advanceRate = this.approvedAdvanceRate;

      return this.approvedRate !== null
        && advanceRate !== '' && advanceRate !== null && advanceRate !== undefined
        && this.hasImportedPrimePayers;
    },
    canAnalyseModelResult() {
      return !!this.creditModelFileId
        && !this.creditModelHasAnalysisData
        && !this.generatingCreditAssessmentReport
        && this.hasApprovedCreditFields;
    },
    canGoToModelReport() {
      return !!this.creditModelFileId && this.creditModelHasAnalysisData;
    },
    showAssessmentProgress() {
      return this.generatingCreditAssessmentReport
        || !!this.assessmentProgress
        || !!this.assessmentError;
    },
    assessmentProgressStatus() {
      return this.assessmentProgress?.status
        || (this.generatingCreditAssessmentReport ? 'processing' : null);
    },
    assessmentProgressStatusClass() {
      const status = this.assessmentProgressStatus;
      if (status === 'completed') return 'completed';
      if (status === 'failed') return 'failed';
      return 'processing';
    },
    assessmentProgressStatusLabel() {
      const status = this.assessmentProgressStatus;
      if (status === 'completed') return 'Completed';
      if (status === 'failed') return 'Failed';
      if (status === 'processing') return 'Processing';
      return 'Processing';
    },
    assessmentProgressMessage() {
      return this.assessmentProgress?.message || null;
    },
    assessmentProgressValue() {
      if (this.assessmentProgress?.progress === undefined
        || this.assessmentProgress?.progress === null) {
        return this.generatingCreditAssessmentReport ? 0 : null;
      }
      return this.assessmentProgress.progress;
    },
    primePayers() {
      const payers = this.organisationDetails.prime_payers
        ?? this.organisationDetails.primePayers
        ?? [];

      if (!Array.isArray(payers)) return [];

      return payers.map((payer) => ({
        name: payer.name ?? payer.Name ?? '',
        parent: payer.parent ?? payer.Parent ?? '',
        sector: payer.sector ?? payer.Sector ?? '',
        moody_rating: payer.moody_rating ?? payer.moodyRating ?? payer['Moody Rating'] ?? ''
      }));
    },
    hasImportedPrimePayers() {
      return this.primePayers.length > 0;
    },
    canImportPrimePayers() {
      return Boolean(
        this.primePayersImportFile
        && this.primePayersCheckResult
        && !this.primePayersChecking
        && !this.primePayersImporting
        && !this.primePayersCheckError
      );
    },
    filteredPrimePayersCheckRows() {
      const rows = Array.isArray(this.primePayersCheckResult?.prime_payers)
        ? this.primePayersCheckResult.prime_payers
        : [];

      if (this.primePayersCheckFilter === 'matched') {
        return rows.filter((row) => row.has_match);
      }
      if (this.primePayersCheckFilter === 'unmatched') {
        return rows.filter((row) => !row.has_match);
      }
      return rows;
    },
    organisationDebts() {
      return Array.isArray(this.organisationDetails.debts)
        ? this.organisationDetails.debts
        : [];
    },
    organisationFields() {
      const details = this.organisationDetails || {};

      return [
        {
          key: 'business_incorporation_date',
          label: 'Business Incorporation Date',
          met: this.hasOrganisationField('business_incorporation_date'),
          displayValue: this.formatDisplayDate(
            details.business_incorporation_date || details.incorporation_date
          )
        },
        {
          key: 'company_credit_score',
          label: 'Company Credit Score',
          optional: true,
          met: this.hasOrganisationField('company_credit_score'),
          displayValue: this.formatPlainValue(details.company_credit_score ?? details.companyCreditScore)
        },
        {
          key: 'owner_industry_experience',
          label: "Owner's Industry Experience (Years)",
          met: this.hasOrganisationField('owner_industry_experience'),
          displayValue: this.formatPlainValue(details.owner_industry_experience)
        },
        {
          key: 'main_sector',
          label: 'Main Sector',
          met: this.hasOrganisationField('main_sector'),
          displayValue: this.formatPlainValue(details.main_sector)
        },
        {
          key: 'subsector',
          label: 'Subsector',
          met: this.hasOrganisationField('subsector'),
          displayValue: this.formatPlainValue(details.subsector)
        }
      ];
    },
    facilityFields() {
      const details = this.organisationDetails || {};

      return [
        {
          key: 'requested_amount',
          label: 'Requested Amount',
          met: this.hasOrganisationField('requested_amount'),
          displayValue: this.formatAmount(details.requested_amount)
        },
        {
          key: 'tenure',
          label: 'Tenure (Months)',
          met: this.hasOrganisationField('tenure'),
          displayValue: this.formatPlainValue(details.tenure ?? details.tenor_months)
        },
        {
          key: 'utilization_rate',
          label: 'Utilization Rate',
          met: this.hasOrganisationField('utilization_rate'),
          displayValue: this.formatPlainValue(details.utilization_rate ?? details.rate)
        },
        {
          key: 'cheque_return',
          label: 'Cheque Return Due to Insufficient Balance',
          met: this.hasOrganisationField('cheque_return'),
          displayValue: this.formatPlainValue(
            details.cheque_return_due_to_insufficient_balance ?? details.cheque_return
          )
        },
        {
          key: 'has_aecb_data',
          label: 'Has AECB Data',
          met: true,
          displayValue: (details.has_aecb_data ?? details.hasAecbData) ? 'Yes' : 'No'
        },
        {
          key: 'scoring_period',
          label: 'Scoring Period',
          met: this.hasOrganisationField('scoring_period'),
          displayValue: this.formatScoringPeriod(details.scoring_period ?? details.scoringPeriod)
        }
      ];
    },
    documentFields() {
      return [
        {
          key: 'kyb',
          label: 'KYB Documents',
          met: this.kybDocuments.length > 0,
          displayValue: this.kybDocuments.length > 0
            ? `${this.kybDocuments.length} file${this.kybDocuments.length !== 1 ? 's' : ''}`
            : '—',
          route: { name: 'chatbot-candidate-kyb', params: { candidateId: this.candidateId } }
        },
        {
          key: 'vat',
          label: 'VAT Statements',
          optional: true,
          met: this.vatDocuments.length > 0,
          displayValue: this.vatDocuments.length > 0
            ? `${this.vatDocuments.length} file${this.vatDocuments.length !== 1 ? 's' : ''}`
            : '—',
          route: { name: 'chatbot-candidate-vat', params: { candidateId: this.candidateId } }
        },
        {
          key: 'bank_statement',
          label: 'Bank Statements / Custom Balances',
          met: this.bankStepMet,
          displayValue: this.bankStatementDocuments.length > 0
            ? `${this.bankStatementDocuments.length} file${this.bankStatementDocuments.length !== 1 ? 's' : ''}`
            : (this.hasCustomBankBalances ? 'Custom balances imported' : '—'),
          route: this.hasCustomBankBalances && this.bankStatementDocuments.length === 0
            ? { name: 'chatbot-candidate-overview', params: { candidateId: this.candidateId } }
            : { name: 'chatbot-candidate-bank-statement', params: { candidateId: this.candidateId } }
        },
        {
          key: 'erp_extract',
          label: 'ERP Extract',
          met: !!this.erpExtractDoc,
          displayValue: this.erpExtractDoc?.name || '—',
          route: { name: 'chatbot-candidate-files', params: { candidateId: this.candidateId } }
        }
      ];
    },
    requirementSections() {
      return [
        {
          key: 'org',
          title: 'Org Details',
          icon: 'las la-building',
          fields: this.organisationFields,
          route: { name: 'chatbot-candidate', params: { candidateId: this.candidateId } }
        },
        {
          key: 'facility',
          title: 'Facility Details',
          icon: 'las la-money-bill-wave',
          fields: this.facilityFields,
          route: { name: 'chatbot-candidate', params: { candidateId: this.candidateId } }
        },
        {
          key: 'documents',
          title: 'Documents',
          icon: 'las la-folder-open',
          fields: this.documentFields,
          route: null
        }
      ];
    },
    overviewStepMet() {
      const coreRequired = [
        ...this.organisationFields.filter(f =>
          ['business_incorporation_date', 'owner_industry_experience', 'main_sector', 'subsector'].includes(f.key)
        ),
        ...this.facilityFields.filter(f =>
          ['requested_amount', 'tenure', 'cheque_return'].includes(f.key)
        ),
        this.documentFields.find(f => f.key === 'erp_extract')
      ];

      return coreRequired.every(item => item?.met);
    },
    vatStepMet() {
      return this.vatDocuments.length > 0;
    },
    hasCustomBankBalances() {
      const details = this.organisationDetails || {};
      const useCustom = !!(
        details.use_custom_bank_statement_balances ?? details.useCustomBankStatementBalances
      );
      if (!useCustom) return false;

      const balances = details.custom_bank_statement_balances
        ?? details.customBankStatementBalances
        ?? [];

      if (!Array.isArray(balances) || balances.length === 0) return false;

      return balances.some((row) => {
        const source = row.balances || row;
        return ['day_7', 'day_14', 'day_22', 'day_30'].some((key) => {
          const value = source[key];
          return value !== '' && value !== null && value !== undefined;
        });
      });
    },
    bankStepMet() {
      return this.bankStatementDocuments.length > 0 || this.hasCustomBankBalances;
    },
    hasTradeLicenseDoc() {
      return this.hasUploadedDocumentType('trade_license');
    },
    hasEmiratesIdDoc() {
      return this.hasUploadedDocumentType('emirates_id');
    },
    analysisPackRequirementsMet() {
      return this.hasTradeLicenseDoc && this.hasEmiratesIdDoc && this.bankStepMet;
    },
    companyAnalysisComplete() {
      return !!this.analysisPackDoc;
    },
    bankStepActionLabel() {
      if (this.bankStatementDocuments.length > 0) return 'View Bank Statements';
      if (this.hasCustomBankBalances) return 'Edit Custom Balances';
      return 'Add Bank Data';
    },
    runModelStepMet() {
      return !!this.creditModelDoc;
    },
    analyseStepMet() {
      return this.creditModelHasAnalysisData;
    },
    /** Steps 1 + 3 must be done before Run Model (VAT is optional). */
    requirementsMet() {
      return this.overviewStepMet && this.bankStepMet;
    },
    canRunModel() {
      if (!this.requirementsMet || this.uploading || this.creditLimitsLoading || this.creditLimitsError) {
        return false;
      }
      if (this.creditLimitsStatus === 'reject') {
        return;
      }
      return true;
    },
    setupGuideSteps() {
      return [
        {
          key: 'overview',
          number: 1,
          title: 'Fill Overview Details',
          description: 'Org details, facility details, debts, and ERP extract.',
          met: this.overviewStepMet,
          locked: false,
          optional: false,
          actionLabel: this.overviewStepMet ? 'Edit Overview' : 'Go to Overview',
          action: () => this.goToOrganisationDetails()
        },
        {
          key: 'vat',
          number: 2,
          title: 'Upload VAT Statements',
          description: 'Upload VAT statements for the candidate.',
          met: this.vatStepMet,
          locked: false,
          optional: true,
          actionLabel: this.vatStepMet ? 'View VAT' : 'Upload VAT',
          action: () => this.$router.push({
            name: 'chatbot-candidate-vat',
            params: { candidateId: this.candidateId }
          })
        },
        {
          key: 'bank',
          number: 3,
          title: 'Bank Statements',
          description: 'Upload bank statements, or import custom bank balances on Overview.',
          met: this.bankStepMet,
          locked: false,
          optional: false,
          actionLabel: this.bankStepActionLabel,
          action: () => this.handleGuideBankStep()
        },
        {
          key: 'run',
          number: 4,
          title: 'Run Model',
          description: 'Run the automated credit model once overview and bank statements are ready.',
          met: this.runModelStepMet,
          locked: !this.requirementsMet,
          optional: false,
          actionLabel: this.runModelStepMet ? 'Re-run Model' : 'Run Model',
          action: () => this.handleGuideRunModel()
        },
        {
          key: 'analyse',
          number: 5,
          title: 'Analyse Model Result',
          description: 'Fill approved advance rate and import prime payers, then analyse the model result.',
          met: this.analyseStepMet,
          locked: !this.runModelStepMet,
          optional: false,
          actionLabel: this.analyseStepMet ? 'View Report' : 'Go to Analyse',
          action: () => this.handleGuideAnalyse()
        },
        {
          key: 'company-analysis',
          number: 6,
          title: 'Company Analysis Pack',
          description: 'Generate the company analysis pack once trade license, Emirates ID, and bank statements are uploaded.',
          met: this.companyAnalysisComplete,
          locked: !this.analysisPackRequirementsMet,
          optional: true,
          actionLabel: this.companyAnalysisComplete ? 'View Analysis Pack' : 'Go to Company Analysis',
          action: () => this.goToCompanyAnalysis()
        },
        {
          key: 'forecasting',
          number: 7,
          title: 'Forecasting',
          description: 'Configure forecasting parameters and generate reports after company analysis is complete.',
          met: false,
          locked: !this.companyAnalysisComplete,
          optional: true,
          actionLabel: 'Go to Forecasting',
          action: () => this.goToForecasting()
        }
      ];
    },
    requiredSetupStepsCompleted() {
      return this.setupGuideSteps.filter(s => !s.optional && s.met).length;
    },
    requiredSetupStepsTotal() {
      return this.setupGuideSteps.filter(s => !s.optional).length;
    },
    uploadStatusText() {
      if (this.uploadStatus === 'uploading') return 'Uploading...';
      if (this.uploadStatus === 'processing') return 'Processing...';
      if (this.uploadStatus === 'completed') return 'Completed';
      if (this.uploadStatus === 'error') return 'Error';
      return 'Ready';
    }
  },
  async mounted() {
    await this.loadInitialData();
    await this.loadCandidate();
  },
  watch: {
    requirementsMet: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.loadCreditLimits();
        } else {
          this.resetCreditLimitsState();
        }
      }
    }
  },
  beforeDestroy() {
    this.disconnectAssessmentWebSocket();
  },
  methods: {
    async loadCreditLimits() {
      if (!this.candidateId || !this.requirementsMet) {
        this.resetCreditLimitsState();
        return;
      }

      this.creditLimitsLoading = true;
      this.creditLimitsError = null;
      try {
        const limits = await ChatbotService.getCreditLimits(this.candidateId);
        this.creditLimits = limits || null;
        this.selectedCreditLimitOption = null;
      } catch (err) {
        console.error('Error loading credit limits:', err);
        this.resetCreditLimitsState();
        this.creditLimitsError = err.response?.data?.message
          || 'Failed to load credit limits. Please try again.';
      } finally {
        this.creditLimitsLoading = false;
      }
    },

    resetCreditLimitsState() {
      this.creditLimits = null;
      this.creditLimitsError = null;
      this.selectedCreditLimitOption = null;
    },

    formatCreditLimit(value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericValue);
    },

    formatWcr(value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      return numericValue.toFixed(2);
    },

    getCreditLimitOptionLabel(option) {
      if (option?.label) return option.label;

      const fallbacks = {
        adjust_wcr_to_match_requested: 'Keep requested limit; accept adjusted WCR',
        wcr_at_1_2: 'Force WCR = 1.2; use resulting approved limit',
        wcr_at_1: 'Force WCR = 1.0; use resulting approved limit'
      };

      return fallbacks[option?.id] || 'Credit limit option';
    },

    isCreditLimitOptionSelected(option) {
      return this.selectedCreditLimitOption?.id === option?.id;
    },

    selectCreditLimitOption(option) {
      if (!option || !option.id) return;
      this.selectedCreditLimitOption = {
        id: option.id,
        wcr: option.wcr,
        credit_limit: option.credit_limit
      };
    },

    async continueWithSelectedOption() {
      if (!this.canContinueWithOption) return;

      this.savingCreditLimitOption = true;
      try {
        const payload = {
          ...(this.organisationDetails || {}),
          adjusted_wcr: this.selectedCreditLimitOption.wcr,
          final_credit_limit: this.selectedCreditLimitOption.credit_limit
        };
        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, payload);
        this.organisationDetails = { ...(saved || payload) };
        await this.sendToModel();
      } catch (error) {
        console.error('Error saving credit limit option:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to save the selected credit limit option. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.savingCreditLimitOption = false;
      }
    },

    hasOrganisationField(field) {
      const details = this.organisationDetails || {};

      switch (field) {
        case 'business_incorporation_date':
          return !!(details.business_incorporation_date || details.incorporation_date);
        case 'requested_amount':
          return details.requested_amount !== undefined &&
            details.requested_amount !== null &&
            details.requested_amount !== '';
        case 'tenure':
          return (details.tenure ?? details.tenor_months) !== undefined &&
            (details.tenure ?? details.tenor_months) !== null &&
            (details.tenure ?? details.tenor_months) !== '';
        case 'main_sector':
          return !!details.main_sector;
        case 'subsector':
          return !!details.subsector;
        case 'owner_industry_experience':
          return details.owner_industry_experience !== undefined &&
            details.owner_industry_experience !== null &&
            details.owner_industry_experience !== '';
        case 'cheque_return':
          return details.cheque_return_due_to_insufficient_balance !== undefined &&
            details.cheque_return_due_to_insufficient_balance !== null &&
            details.cheque_return_due_to_insufficient_balance !== '';
        case 'company_credit_score': {
          const val = details.company_credit_score ?? details.companyCreditScore;
          return val !== undefined && val !== null && val !== '';
        }
        case 'utilization_rate': {
          const val = details.utilization_rate ?? details.rate;
          return val !== undefined && val !== null && val !== '';
        }
        case 'scoring_period': {
          const sp = details.scoring_period ?? details.scoringPeriod;
          return !!(sp?.end_year && sp?.end_month);
        }
        default:
          return false;
      }
    },

    formatScoringPeriod(sp) {
      if (!sp?.end_year || !sp?.end_month) return '—';

      const monthAbbr = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
      const startLabel = sp.start_month && sp.start_year
        ? `${monthAbbr[sp.start_month - 1]} ${String(sp.start_year).slice(-2)}`
        : null;
      const endLabel = `${monthAbbr[sp.end_month - 1]} ${String(sp.end_year).slice(-2)}`;

      return startLabel ? `${startLabel} – ${endLabel}` : endLabel;
    },

    formatDisplayDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatPlainValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    },

    formatAmount(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    formatDebtAmount(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatPrimePayerValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    },

    formatMatchScore(score) {
      const numeric = Number(score);
      if (!Number.isFinite(numeric)) return '—';
      return `${Math.round(numeric * 100)}%`;
    },

    resetPrimePayersCheckState() {
      this.primePayersCheckResult = null;
      this.primePayersCheckError = null;
      this.primePayersCheckFilter = 'all';
    },

    onPrimePayersFileChange(event) {
      const file = event.target.files?.[0];
      this.primePayersImportFile = file || null;
      this.resetPrimePayersCheckState();
    },

    clearPrimePayersFileInput() {
      this.primePayersImportFile = null;
      this.resetPrimePayersCheckState();
      if (this.$refs.primePayersFileInput) {
        this.$refs.primePayersFileInput.value = '';
      }
    },

    async checkPrimePayers() {
      if (!this.candidateId || !this.primePayersImportFile || this.primePayersChecking) {
        return;
      }

      this.primePayersChecking = true;
      this.primePayersCheckError = null;
      this.primePayersCheckResult = null;

      try {
        const result = await ChatbotService.checkPrimePayers(
          this.candidateId,
          this.primePayersImportFile
        );

        const rows = Array.isArray(result?.prime_payers)
          ? result.prime_payers.map((row) => ({
            name: row?.name ?? null,
            parent: row?.parent ?? null,
            sector: row?.sector ?? null,
            moody_rating: row?.moody_rating ?? null,
            ContactID: row?.ContactID ?? null,
            has_match: Boolean(row?.has_match),
            matching_prime_payers: Array.isArray(row?.matching_prime_payers)
              ? row.matching_prime_payers
              : []
          }))
          : [];

        this.primePayersCheckResult = {
          prime_payers: rows,
          total: Number.isFinite(Number(result?.total)) ? Number(result.total) : rows.length,
          matched_count: Number.isFinite(Number(result?.matched_count))
            ? Number(result.matched_count)
            : rows.filter((row) => row.has_match).length,
          unmatched_count: Number.isFinite(Number(result?.unmatched_count))
            ? Number(result.unmatched_count)
            : rows.filter((row) => !row.has_match).length
        };
        this.primePayersCheckFilter = 'all';
      } catch (error) {
        console.error('Error checking prime payers:', error);
        this.primePayersCheckError = error.response?.data?.message
          || 'Failed to check prime payers Excel. Please try again.';
        this.$bvToast.toast(this.primePayersCheckError, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.primePayersChecking = false;
      }
    },

    populateApprovedCreditFields(details) {
      const source = details || {};
      const advanceRate = source.approved_advance_rate ?? source.approvedAdvanceRate;

      this.approvedAdvanceRate = advanceRate !== undefined && advanceRate !== null && advanceRate !== ''
        ? String(advanceRate)
        : '';
    },

    async saveApprovedCreditFields() {
      if (!this.candidateId || this.savingApprovedCreditFields) {
        return;
      }

      this.savingApprovedCreditFields = true;

      try {
        const payload = { ...this.organisationDetails };

        if (this.approvedAdvanceRate !== '' && this.approvedAdvanceRate !== null) {
          payload.approved_advance_rate = Number(this.approvedAdvanceRate);
        } else {
          delete payload.approved_advance_rate;
        }

        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, payload);
        this.organisationDetails = { ...(saved || payload) };
        this.populateApprovedCreditFields(this.organisationDetails);

        this.$bvToast.toast('Credit assessment fields saved.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error saving credit assessment fields:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to save credit assessment fields. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.savingApprovedCreditFields = false;
      }
    },

    async importPrimePayers() {
      if (!this.candidateId || !this.primePayersImportFile || this.primePayersImporting) {
        return;
      }

      this.primePayersImporting = true;

      try {
        const result = await ChatbotService.importPrimePayers(
          this.candidateId,
          this.primePayersImportFile
        );

        const details = await ChatbotService.getOrganisationDetails(this.candidateId);
        const importedPayers = Array.isArray(result?.prime_payers)
          ? result.prime_payers
          : (Array.isArray(details?.prime_payers) ? details.prime_payers : []);

        this.organisationDetails = {
          ...(details || {}),
          prime_payers: importedPayers
        };

        this.$bvToast.toast('Prime payers imported successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });

        this.clearPrimePayersFileInput();
      } catch (error) {
        console.error('Error importing prime payers:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to import prime payers. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.primePayersImporting = false;
      }
    },

    async deleteImportedPrimePayers() {
      if (!this.candidateId || !this.hasImportedPrimePayers || this.primePayersDeleting) {
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete the imported prime payers? This action cannot be undone.',
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) return;

      this.primePayersDeleting = true;

      try {
        // Backend merges organisation_details; omitting the key leaves old data.
        // Explicitly clear with an empty list.
        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, {
          prime_payers: []
        });

        this.organisationDetails = {
          ...(saved || this.organisationDetails),
          prime_payers: []
        };
        if (Object.prototype.hasOwnProperty.call(this.organisationDetails, 'primePayers')) {
          this.$delete(this.organisationDetails, 'primePayers');
        }

        this.clearPrimePayersFileInput();

        this.$bvToast.toast('Imported prime payers deleted.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error deleting imported prime payers:', error);
        this.$bvToast.toast(
          error.response?.data?.message || 'Failed to delete imported prime payers. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.primePayersDeleting = false;
      }
    },

    async generateCreditAssessmentReport() {
      if (!this.canAnalyseModelResult) {
        return;
      }

      this.generatingCreditAssessmentReport = true;
      this.assessmentError = null;
      this.assessmentProgress = {
        status: 'processing',
        message: 'Connecting...',
        progress: 0,
        step: null,
        error: null
      };

      this.disconnectAssessmentWebSocket();

      try {
        await this.connectAssessmentWebSocketAndStartJob();
      } catch (error) {
        this.handleAssessmentStartError(error);
      }
    },

    connectAssessmentWebSocketAndStartJob() {
      return new Promise((resolve, reject) => {
        const wsUrl = `${process.env.VUE_APP_SOCKET_URL}/automated-assessment/${this.candidateId}/`;
        this.assessmentWebSocketReady = false;
        this.assessmentWebsocket = new WebSocket(wsUrl);

        const connectionTimeout = setTimeout(() => {
          reject(new Error('WebSocket connection timed out. Please try again.'));
        }, 15000);

        this.assessmentWebsocket.onopen = async () => {
          clearTimeout(connectionTimeout);

          try {
            this.assessmentProgress = {
              status: 'processing',
              message: 'Starting automated assessment analysis...',
              progress: 0,
              step: null,
              error: null
            };

            await ChatbotService.analyzeAutomatedAssessment(this.candidateId, {
              file_id: this.creditModelFileId
            });

            this.assessmentWebSocketReady = true;
            resolve();
          } catch (error) {
            reject(error);
          }
        };

        this.assessmentWebsocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleAssessmentWebSocketMessage(data);
          } catch (error) {
            console.error('Error parsing automated assessment WebSocket message:', error);
          }
        };

        this.assessmentWebsocket.onerror = () => {
          clearTimeout(connectionTimeout);

          if (!this.assessmentWebSocketReady) {
            reject(new Error('WebSocket connection failed. Please try again.'));
            return;
          }

          if (this.generatingCreditAssessmentReport) {
            this.handleAssessmentConnectionLost();
          }
        };

        this.assessmentWebsocket.onclose = () => {
          if (this.generatingCreditAssessmentReport && this.assessmentWebSocketReady) {
            this.handleAssessmentConnectionLost();
          }
        };
      });
    },

    handleAssessmentWebSocketMessage(data) {
      if (data.file_id && this.creditModelFileId && data.file_id !== this.creditModelFileId) {
        return;
      }

      const status = data.status || data.type;

      if (status === 'processing') {
        this.assessmentProgress = {
          status: 'processing',
          message: data.message || this.getAssessmentStepLabel(data.step),
          progress: data.progress ?? this.assessmentProgress?.progress ?? 0,
          step: data.step || null,
          error: null
        };
        return;
      }

      if (status === 'completed') {
        this.assessmentProgress = {
          status: 'completed',
          message: data.message || 'Automated assessment analysis completed successfully',
          progress: 100,
          step: 'completed',
          error: null
        };
        this.completeAssessmentRun(data.analysis_data);
        return;
      }

      if (status === 'failed') {
        const errorMessage = data.error || data.message || 'Automated assessment analysis failed.';
        this.assessmentProgress = {
          status: 'failed',
          message: data.message || errorMessage,
          progress: data.progress ?? 0,
          step: data.step || null,
          error: errorMessage
        };
        this.failAssessmentRun(errorMessage);
      }
    },

    completeAssessmentRun() {
      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = null;
      this.creditModelHasAnalysisData = true;
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast('Credit assessment analysis completed successfully.', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      });

      this.goToCreditModelReport();
    },

    goToCreditModelReport() {
      if (!this.creditModelFileId) {
        return;
      }

      this.$router.push({
        name: 'chatbot-report-details',
        params: {
          candidateId: this.candidateId,
          fileId: this.creditModelFileId
        },
        query: {
          reportType: 'credit_model'
        }
      });
    },

    goToPayerReport() {
      this.$router.push({
        name: 'chatbot-payer-report-details',
        params: {
          candidateId: this.candidateId
        }
      });
    },

    failAssessmentRun(errorMessage) {
      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = errorMessage;
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast(errorMessage, {
        title: 'Analysis failed',
        variant: 'danger',
        solid: true,
        autoHideDelay: 6000
      });
    },

    handleAssessmentStartError(error) {
      console.error('Error starting credit assessment analysis:', error);
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to start credit assessment analysis. Please try again.';

      this.generatingCreditAssessmentReport = false;
      this.assessmentWebSocketReady = false;
      this.assessmentError = errorMessage;
      this.assessmentProgress = {
        status: 'failed',
        message: errorMessage,
        progress: 0,
        step: null,
        error: errorMessage
      };
      this.disconnectAssessmentWebSocket();

      this.$bvToast.toast(errorMessage, {
        title: 'Error',
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000
      });
    },

    handleAssessmentConnectionLost() {
      if (!this.generatingCreditAssessmentReport) {
        return;
      }

      const errorMessage = 'Connection lost while analysis was in progress. Please retry.';
      this.failAssessmentRun(errorMessage);
      this.assessmentProgress = {
        status: 'failed',
        message: errorMessage,
        progress: this.assessmentProgress?.progress ?? 0,
        step: this.assessmentProgress?.step || null,
        error: errorMessage
      };
    },

    disconnectAssessmentWebSocket() {
      if (!this.assessmentWebsocket) {
        return;
      }

      this.assessmentWebsocket.onopen = null;
      this.assessmentWebsocket.onmessage = null;
      this.assessmentWebsocket.onerror = null;
      this.assessmentWebsocket.onclose = null;

      if (this.assessmentWebsocket.readyState === WebSocket.OPEN
        || this.assessmentWebsocket.readyState === WebSocket.CONNECTING) {
        this.assessmentWebsocket.close();
      }

      this.assessmentWebsocket = null;
      this.assessmentWebSocketReady = false;
    },

    getAssessmentStepLabel(step) {
      if (!step) {
        return 'Starting automated assessment analysis...';
      }

      return ASSESSMENT_STEP_LABELS[step] || 'Processing analysis...';
    },

    getAssessmentStatusIcon(status) {
      if (status === 'completed') return 'las la-check-circle';
      if (status === 'failed') return 'las la-times-circle';
      return 'las la-spinner la-spin';
    },

    async loadInitialData() {
      if (!this.candidateId) {
        this.error = 'Candidate ID is required';
        return;
      }

      this.loading = true;
      this.error = null;
      this.organisationDetails = {};
      this.erpExtractDoc = null;
      this.creditModelDoc = null;
      this.creditModelHasAnalysisData = false;
      this.vatDocuments = [];
      this.bankStatementDocuments = [];
      this.kybDocuments = [];
      this.clearPrimePayersFileInput();

      const toList = (res) => Array.isArray(res)
        ? res
        : (res?.data || res?.files || res?.uploads || []);

      try {
        const [details, uploadsResponse, creditModelResponse, vatRes, bankRes, kybRes, analysisPackRes] = await Promise.all([
          ChatbotService.getOrganisationDetails(this.candidateId),
          ChatbotService.getUploads(this.candidateId, {
            type: 'erp_extract',
            page: 1,
            page_size: 1
          }),
          ChatbotService.getUploads(this.candidateId, {
            type: 'credit_model',
            page: 1,
            page_size: 10
          }),
          ChatbotService.getUploads(this.candidateId, {
            type: 'vat_statement',
            page: 1,
            page_size: 10
          }),
          ChatbotService.getUploads(this.candidateId, {
            type: 'bank_statement',
            page: 1,
            page_size: 10
          }),
          ChatbotService.getUploads(this.candidateId, {
            type: 'trade_license,emirates_id,aecb_report,moa,aoa,audit_report',
            page: 1,
            page_size: 50
          }),
          ChatbotService.getUploads(this.candidateId, {
            type: 'analysis_pack',
            page: 1,
            page_size: 10
          })
        ]);

        this.organisationDetails = details || {};
        this.populateApprovedCreditFields(this.organisationDetails);

        const documents = toList(uploadsResponse);
        this.erpExtractDoc = documents[0] || null;

        const creditModelDocuments = toList(creditModelResponse);
        this.creditModelDoc = this.getLatestUpload(creditModelDocuments);

        this.vatDocuments = toList(vatRes);
        this.bankStatementDocuments = toList(bankRes);
        this.kybDocuments = toList(kybRes);
        this.analysisPackDoc = this.getLatestUpload(toList(analysisPackRes));

        await this.loadCreditModelAnalysisState();
      } catch (err) {
        console.error('Error loading organisation details:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load organisation details.';
      } finally {
        this.loading = false;
      }
    },

    async loadCandidate() {
      if (!this.candidateId) return;

      try {
        this.candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
      } catch (err) {
        console.error('Error loading candidate:', err);
      }
    },

    async buildErpModelFiles() {
      const fileId = this.erpExtractDoc?.id || this.erpExtractDoc?.file_id;
      if (!fileId) {
        throw new Error('ERP Extract is required.');
      }

      const blob = await ChatbotService.downloadFile(fileId);
      const buffer = await blob.arrayBuffer();
      const workbook = read(buffer, { type: 'array' });
      const sheetNames = workbook.SheetNames;

      if (sheetNames.length < 4) {
        throw new Error('ERP Extract must contain at least 4 sheets for invoice, payables, payments, and bill payment data.');
      }

      const baseName = this.erpExtractDoc.name || 'erp_extract.xlsx';
      const fileKeys = ['invoice', 'payables', 'payments', 'billPayment'];
      const files = {};

      fileKeys.forEach((key, index) => {
        const sheetName = sheetNames[index];
        const singleSheetWorkbook = utils.book_new();
        utils.book_append_sheet(
          singleSheetWorkbook,
          workbook.Sheets[sheetName],
          sheetName
        );
        const output = write(singleSheetWorkbook, { bookType: 'xlsx', type: 'array' });
        files[key] = new File(
          [output],
          `${key}_${baseName}`,
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        );
      });

      return files;
    },

    buildCreditModelParams() {
      const details = this.organisationDetails || {};

      return {
        requested_amount: parseFloat(details.requested_amount) || 0,
        tenor_months: parseInt(details.tenure ?? details.tenor_months, 10) || 0,
        owner_industry_experience: parseInt(details.owner_industry_experience, 10) || 0,
        cheque_return_due_to_insufficient_balance: parseInt(
          details.cheque_return_due_to_insufficient_balance ?? details.cheque_return,
          10
        ) || 0
      };
    },

    async sendToModel() {
      if (!this.requirementsMet) {
        this.$bvToast.toast('Please complete all organisation details first.', {
          title: 'Missing Information',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }

      if (
        (this.creditLimitsStatus === 'choose_option' || this.creditLimitsStatus === 'reject')
        && !this.selectedCreditLimitOption
      ) {
        return;
      }

      this.uploading = true;
      this.uploadStatus = 'processing';
      this.uploadProgress = 0;
      this.uploadError = null;

      try {
        const erpFiles = await this.buildErpModelFiles();
        this.payload = await ChatbotService.buildCreditModelPayload(this.candidateId);

        const details = this.organisationDetails || {};
        this.payload.sector_classification = {
          main_sector: details.main_sector,
          subsector: details.subsector
        };
        this.payload.credit_model_params = this.buildCreditModelParams();
        this.payload.organisation_details = details;
        if (this.selectedCreditLimitOption) {
          this.payload.credit_limit_option = this.selectedCreditLimitOption;
        }
        this.payload.forecasting_parameters = {
          sales_increase: 0,
          payments_increase: 0
        };

        const formData = new FormData();
        formData.append('invoice_file', erpFiles.invoice);
        formData.append('payables_file', erpFiles.payables);
        formData.append('payments_file', erpFiles.payments);
        formData.append('bill_payment_file', erpFiles.billPayment);
        formData.append('payload', JSON.stringify(this.payload));
        formData.append('candidate_id', this.candidateId);

        this.uploadStatus = 'uploading';
        const response = await ChatbotService.submitCreditModelReport(
          this.candidateId,
          formData,
          (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        );

        this.uploadStatus = 'completed';
        this.uploadProgress = 100;

        this.$bvToast.toast('Model run completed successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000
        });

        if (response?.upload_id) {
          const uploadId = response.upload_id;
          const fileName = `${this.candidate?.name || 'candidate'}_automated_assessment_report_${new Date().toISOString().split('T')[0]}.xlsx`;
          const fileResponse = await ChatbotService.downloadFile(uploadId);
          const url = window.URL.createObjectURL(new Blob([fileResponse]));
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$router.push({
            name: 'chatbot-file-details',
            params: {
              candidateId: this.candidateId,
              fileId: uploadId
            },
            query: {
              autoDownloadStarted: '1',
              downloadFileName: fileName
            }
          });
        }
      } catch (error) {
        console.error('Error running model:', error);
        this.uploadStatus = 'error';
        const errorMessage = error.response?.data?.message || error.message || 'Failed to run model. Please try again.';
        this.uploadError = errorMessage;

        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.uploading = false;
      }
    },

    getLatestUpload(documents) {
      const processedDocs = (Array.isArray(documents) ? documents : []).map((doc) => ({
        ...doc,
        status: doc.scan_status || doc.status || 'pending'
      }));

      if (processedDocs.length === 0) {
        return null;
      }

      return processedDocs.reduce((latest, doc) => {
        const docDate = new Date(doc.uploaded_at || doc.scan_completed_at || 0);
        const latestDate = new Date(latest.uploaded_at || latest.scan_completed_at || 0);
        return docDate > latestDate ? doc : latest;
      });
    },

    formatUploadDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getDocumentStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },

    getDocumentStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing' || status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },

    viewCreditModelDocument() {
      const fileId = this.creditModelDoc?.id || this.creditModelDoc?.file_id;
      if (!fileId) return;

      this.$router.push({
        name: 'chatbot-file-details',
        params: {
          candidateId: this.candidateId,
          fileId
        }
      });
    },

    async downloadCreditModelDocument() {
      const fileId = this.creditModelDoc?.id || this.creditModelDoc?.file_id;
      if (!fileId || this.downloadingCreditModelDoc) return;

      this.downloadingCreditModelDoc = true;

      try {
        const blob = await ChatbotService.downloadFile(fileId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.creditModelDoc?.name || this.creditModelDoc?.file_name || 'credit_model_report.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading credit model document:', err);
        this.$bvToast.toast('Failed to download credit model document. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.downloadingCreditModelDoc = false;
      }
    },

    async loadCreditModelAnalysisState() {
      this.creditModelHasAnalysisData = false;

      if (!this.creditModelDoc) {
        return;
      }

      const fileId = this.creditModelDoc.id || this.creditModelDoc.file_id;
      if (!fileId) {
        return;
      }

      try {
        const fileDetails = await ChatbotService.getFileDetails(fileId);
        const transaction = fileDetails.transactions?.[0];
        this.creditModelHasAnalysisData = this.hasCreditModelAnalysisData(transaction);
      } catch (err) {
        console.error('Error loading credit model analysis state:', err);
      }
    },

    hasCreditModelAnalysisData(transaction) {
      const analysisData = transaction?.analysis_data;
      if (!analysisData || typeof analysisData !== 'object') {
        return false;
      }

      const sectionKeys = [
        'key_strengths',
        'key_weaknesses',
        'revenue_trends',
        'cash_quick_ratio',
        'relative_risk_strengths',
        'relative_risk_weaknesses',
        'conclusion_points',
        'recommendation_points',
        'payer_scores',
        'credit_recommendation'
      ];

      return sectionKeys.some((key) => {
        const value = analysisData[key];
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      });
    },

    getStatusIcon(status) {
      switch (status) {
        case 'completed':
          return 'las la-check-circle';
        case 'error':
          return 'las la-times-circle';
        case 'uploading':
        case 'processing':
          return 'las la-spinner la-spin';
        default:
          return 'las la-info-circle';
      }
    },

    goToOrganisationDetails() {
      this.$router.push({
        name: 'chatbot-candidate-overview',
        params: { candidateId: this.candidateId }
      });
    },

    goToRequestedAmount() {
      this.$router.push({
        name: 'chatbot-candidate-overview',
        params: { candidateId: this.candidateId },
        query: { focus: 'requested-amount' }
      });
    },

    handleGuideBankStep() {
      if (this.hasCustomBankBalances && this.bankStatementDocuments.length === 0) {
        this.goToOrganisationDetails();
        return;
      }

      this.$router.push({
        name: 'chatbot-candidate-bank-statement',
        params: { candidateId: this.candidateId }
      });
    },

    handleGuideRunModel() {
      if (!this.requirementsMet) return;
      this.scrollToSection('runModelSection');
    },

    handleGuideAnalyse() {
      if (!this.runModelStepMet) return;
      this.$router.push({
        name: 'chatbot-automated-assessment-report',
        params: { candidateId: this.candidateId }
      });
    },

    hasUploadedDocumentType(type) {
      return this.kybDocuments.some((doc) => {
        const fileType = doc.type || doc.document_type;
        if (type === 'trade_license') {
          return fileType === 'trade_license' || fileType === 'trade_licence';
        }
        return fileType === type;
      });
    },

    goToCompanyAnalysis() {
      if (!this.analysisPackRequirementsMet) return;

      this.$router.push({
        name: 'chatbot-candidate-analysis-pack',
        params: { candidateId: this.candidateId }
      });
    },

    goToForecasting() {
      if (!this.companyAnalysisComplete) return;

      this.$router.push({
        name: 'chatbot-candidate-forecasting',
        params: { candidateId: this.candidateId }
      });
    },

    scrollToSection(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

.automated-assessment-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.assessment-header {
  padding: 1rem 2rem;
  flex-shrink: 0;
}

.assessment-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-family: $brand-font-medium;

  i {
    color: $color-primary;
    font-size: 2rem;
  }
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
  width: 100%;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 0;
}

.error-state,
.requirements-state {
  padding: 1.5rem 0;
}

.setup-guide {
  list-style: none;
  margin: 0 0 2rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.setup-guide-step {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border: 1px solid #e6e9f0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;

  &:hover {
    box-shadow: 0 8px 20px -10px rgba(16, 24, 40, 0.18);
  }

  &.met {
    background: #e9f9f0;
    border-color: #bfe6cd;

    .setup-guide-number {
      background: #0f9d58;
      color: #fff;
    }
  }

  &.locked:not(.met) {
    opacity: 0.55;
    background: #fafafa;
  }
}

.setup-guide-number {
  width: 2rem;
  height: 2rem;
  border-radius: 6px;
  background: $color-primary;
  color: $color-secondary;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: $brand-font-medium;
  flex-shrink: 0;

  i {
    font-size: 1rem;
  }
}

.setup-guide-body {
  flex: 1;
  min-width: 0;
}

.setup-guide-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.setup-guide-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.setup-guide-locked-label {
  font-size: 0.7rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.setup-guide-description {
  margin: 0;
  font-size: 0.8rem;
  color: #718096;
}

.setup-guide-action {
  width: 11.5rem;
  flex-shrink: 0;
}

.req-dashboard-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.75rem;

  > i {
    font-size: 2rem;
    color: $color-primary;
    flex-shrink: 0;
    margin-top: 2px;

    &.req-icon-ok {
      color: #16a34a;
    }
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 0.25rem;
    font-family: $brand-font-medium;
  }

  p {
    color: #718096;
    font-size: 0.875rem;
    margin: 0;
  }
}

.req-sections {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.req-section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.req-section-documents {
  grid-column: 1 / -1;
  border-color: rgba($color-primary, 0.35);
  box-shadow: 0 1px 0 rgba($color-primary, 0.08);

  .req-section-head {
    background: rgba($color-primary, 0.06);
    padding: 0.9rem 1.25rem;

    > i {
      font-size: 1.35rem;
    }

    > span:nth-child(2) {
      font-size: 0.95rem;
    }
  }
}

.req-doc-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  padding: 1rem 1.25rem 1.25rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
}

.req-doc-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.875rem;
  padding: 1.15rem;
  border: 1px solid #e6e9f0;
  border-radius: 14px;
  background: #fff;
  min-height: 120px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease;

  &:hover {
    box-shadow: 0 10px 24px -12px rgba(16, 24, 40, 0.2);
    transform: translateY(-1px);
  }

  &.met {
    border-color: #bfe6cd;
    background: #f0fdf4;
  }

  &:not(.met):not(.optional) {
    border-color: rgba(220, 38, 38, 0.3);
    background: #fef2f2;
  }
}

.req-doc-card-top {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.req-doc-status {
  font-size: 1.15rem;
  flex-shrink: 0;
  margin-top: 1px;
  color: #dc2626;

  .req-doc-card.met & {
    color: #16a34a;
  }
}

.req-doc-card-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.req-doc-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.req-doc-value {
  font-size: 0.78rem;
  color: #4a5568;
}

.req-doc-missing {
  font-size: 0.78rem;
  color: #b91c1c;
}

.req-section-head {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;

  > i {
    font-size: 1.1rem;
    color: $color-primary;
  }

  > span:nth-child(2) {
    flex: 1;
    font-size: 0.875rem;
    font-weight: 600;
    color: #2d3748;
    font-family: $brand-font-medium;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}

.req-section-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;

  &.complete {
    background: #dcfce7;
    color: #15803d;
  }

  &.incomplete {
    background: #fee2e2;
    color: #b91c1c;
  }
}

.req-field-list {
  list-style: none;
  padding: 0.5rem 0;
  margin: 0;
  flex: 1;
}

.req-field-item {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  color: #4a5568;

  > i {
    font-size: 0.95rem;
    flex-shrink: 0;
    color: #dc2626;
  }

  &.met > i {
    color: #16a34a;
  }
}

.req-field-label {
  flex: 1;
  color: inherit;
}

.req-field-value {
  color: #4a5568;
  font-family: $brand-font-medium;
  font-size: 0.78rem;
  white-space: nowrap;
}

.req-field-optional {
  font-size: 0.7rem;
  color: #a0aec0;
  font-style: italic;
}

.req-section-action {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid $color-primary;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state p {
  color: #e53e3e;
  font-size: 1rem;
  margin: 0 0 1rem 0;
}

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0;
  color: #dc2626;
  font-size: 0.875rem;

  &.met {
    color: #16a34a;
  }

  i {
    font-size: 1rem;
  }
}

.org-details-section {
  margin-bottom: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
  padding-bottom: 0.5rem;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.section-description {
  margin: 0 0 0.75rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.details-list {
  margin-top: 0.7rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.detail-row {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.detail-label {
  flex-shrink: 0;
  color: #4a5568;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: $brand-font-regular;
  letter-spacing: 0.01em;
}

.detail-value {
  color: #1a202c;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: $brand-font-medium;
}

.debts-section {
  margin-top: 1.2rem;
}

.credit-model-section {
  margin-top: 1.2rem;
}

.credit-assessment-report-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
}

.approved-credit-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.approved-credit-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .form-label {
    position: relative;
    top: 5px;
    left: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.045em;
    color: #667085;
    font-family: $brand-font-semi-bold;
  }
}

.rate-display-card {
  width: 100%;
  min-height: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  color: #2d3748;
  background: #f8fafc;
  font-family: $brand-font-regular;
  overflow: hidden;
}

.rate-display-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d3748;
  line-height: 1.25;
}

.rate-display-placeholder {
  font-size: 0.875rem;
  line-height: 1.25;
  color: #a0aec0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-input {
  width: 100%;
  min-height: 38px;
  height: 38px;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.25;
  color: #2d3748;
  background: #ffffff;
  font-family: $brand-font-regular;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.15);
  }
}

.prime-payers-import {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.prime-payers-import-hint {
  margin: 0;
  font-size: 0.8125rem;
  color: #718096;
  font-family: $brand-font-regular;
  line-height: 1.4;
}

.prime-payers-import-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.prime-payers-file-input-container {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 360px;
  height: 42px;
  border: 1px dashed #cbd5e0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.05);
  }
}

.prime-payers-file-input {
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.prime-payers-file-input-overlay {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  pointer-events: none;
  overflow: hidden;
  max-width: 100%;
}

.prime-payers-file-icon {
  flex-shrink: 0;
  font-size: 1.125rem;
  color: $color-primary;
}

.prime-payers-file-hint {
  font-size: 0.8125rem;
  color: #718096;
  font-family: $brand-font-regular;
  white-space: nowrap;
}

.prime-payers-file-selected {
  font-size: 0.8125rem;
  color: #2d3748;
  font-family: $brand-font-medium;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prime-payers-check-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  background: #fff5f5;
  color: #c53030;
  font-size: 0.875rem;
  line-height: 1.4;

  i {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
}

.prime-payers-check-loading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.5rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #4a5568;
  font-size: 0.875rem;
}

.prime-payers-check-results {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fbfcfe;
}

.prime-payers-check-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.prime-payers-check-stat {
  min-width: 96px;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eef1f6;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.prime-payers-check-stat-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a202c;
  font-family: $brand-font-bold;
  line-height: 1.2;
}

.prime-payers-check-stat-label {
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-family: $brand-font-semi-bold;
}

.prime-payers-check-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.prime-payers-filter-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #4a5568;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.8125rem;
  font-family: $brand-font-medium;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #cbd5e0;
    color: #1a202c;
  }

  &.active {
    background: rgba($color-primary, 0.12);
    border-color: rgba($color-primary, 0.45);
    color: darken($color-primary, 28%);
  }
}

.prime-payers-check-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 360px;
  overflow-y: auto;
}

.prime-payers-check-row {
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 0.75rem 0.875rem;

  &.unmatched {
    border-color: #fbd38d;
    background: #fffaf0;
  }

  &.matched {
    border-color: #c6f6d5;
  }
}

.prime-payers-check-row-header {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.prime-payers-check-row-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.prime-payers-check-row-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a202c;
  font-family: $brand-font-semi-bold;
}

.prime-payers-check-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.matched {
    background: rgba(22, 163, 74, 0.12);
    color: #15803d;
  }

  &.unmatched {
    background: rgba(217, 119, 6, 0.12);
    color: #b45309;
  }
}

.prime-payers-check-row-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: #718096;
}

.prime-payers-match-list {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid #edf2f7;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.prime-payers-match-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.prime-payers-match-item {
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  background: #f7fafc;
  border: 1px solid #edf2f7;
}

.prime-payers-match-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
}

.prime-payers-match-score {
  font-size: 0.75rem;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
}

.prime-payers-match-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.25rem;
  font-size: 0.78125rem;
  color: #718096;
}

.prime-payers-check-empty {
  padding: 1rem;
  text-align: center;
  color: #718096;
  font-size: 0.875rem;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.prime-payers-section {
  margin-top: 0.75rem;
}

.prime-payers-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 1rem;

  .subsection-title {
    margin: 0;
  }
}

.prime-payers-table-wrapper {
  overflow-x: auto;
}

.credit-assessment-report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-start;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.analyse-prereq-hint {
  flex: 1 1 100%;
  margin: 0;
  font-size: 0.85rem;
  color: #b91c1c;
}

.assessment-progress-section {
  margin-top: 1.25rem;
  padding: 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.assessment-progress-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.assessment-progress-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: $brand-font-medium;

  &.processing {
    color: #3b82f6;
  }

  &.completed {
    color: #16a34a;
  }

  &.failed {
    color: #dc2626;
  }
}

.assessment-progress-message {
  color: #4a5568;
  font-size: 0.875rem;
  line-height: 1.5;
}

.assessment-progress-bar-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.assessment-progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.assessment-progress-bar-fill {
  height: 100%;
  background: $color-primary;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.assessment-progress-percentage {
  min-width: 2.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4a5568;
  text-align: right;
}

.assessment-progress-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
  line-height: 1.5;
}

.prime-payers-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;

  thead {
    background: #f8f9fa;
    border-bottom: 1px solid #e2e8f0;

    th {
      padding: 0.625rem 0.875rem;
      text-align: left;
      font-size: 0.6875rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
  }

  tbody td {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    color: #2d3748;
    border-bottom: 1px solid #f1f3f5;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.document-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.document-icon-large {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.25rem;
    color: $color-primary;
  }
}

.document-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.document-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  font-family: $brand-font-regular;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;

  &.status-completed {
    color: #16a34a;

    i {
      color: #16a34a;
    }
  }

  &.status-processing {
    color: #3b82f6;

    i {
      color: #3b82f6;
    }
  }

  &.status-error {
    color: #dc2626;

    i {
      color: #dc2626;
    }
  }

  &.status-pending {
    color: #6b7280;

    i {
      color: #6b7280;
    }
  }

  i {
    font-size: 1.3rem;
  }
}

.view-btn {
  background: transparent;
  border: none;
  color: #4a5568;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover i {
    color: darken(#4a5568, 10%);
  }

  i {
    font-size: 1.3rem;
  }
}

.subsection-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.debts-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;

  thead {
    background: #f8f9fa;
    border-bottom: 1px solid #e2e8f0;

    th {
      padding: 0.625rem 0.875rem;
      text-align: left;
      font-size: 0.6875rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }
  }

  tbody td {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    color: #2d3748;
    border-bottom: 1px solid #f1f3f5;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.progress-section {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.progress-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  margin-bottom: 0.75rem;

  &.completed {
    color: #16a34a;
  }

  &.error {
    color: #dc2626;
  }
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: $color-primary;
  transition: width 0.3s ease;
}

.progress-percentage {
  font-size: 0.8125rem;
  color: #718096;
  min-width: 2.5rem;
}

.progress-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  color: #dc2626;
  font-size: 0.875rem;
}

.run-model-credit-limits {
  margin-top: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
}

.run-model-credit-limits-loading {
  color: #94a3b8;
  font-size: 0.875rem;
  font-family: $brand-font-regular;
}

.credit-limits-error {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #b91c1c;
  font-size: 0.9rem;

  i {
    font-size: 1.25rem;
    margin-top: 0.1rem;
  }

  p {
    margin: 0 0 0.5rem;
  }
}

.credit-limits-message {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  border: 1px solid transparent;

  i {
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.45;
    font-family: $brand-font-regular;
  }

  &.success {
    background: #f0fdf4;
    border-color: #bbf7d0;

    i { color: #15803d; }
    p { color: #166534; }
  }

  &.warning {
    background: #fffbeb;
    border-color: #fde68a;

    i { color: #d97706; }
    p { color: #92400e; }
  }

  &.error {
    background: #fef2f2;
    border-color: #fecaca;

    i { color: #dc2626; }
    p { color: #991b1b; }
  }
}

.run-model-credit-limits-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;

  &.has-wcr {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.run-model-credit-limit {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.run-model-credit-label {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.run-model-credit-subtext {
  display: inline;
  margin-left: 0.35rem;
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 400;
  font-family: $brand-font-regular;
  text-transform: none;
  letter-spacing: 0;
}

.run-model-credit-value {
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;

  &.approved {
    color: #15803d;
  }
}

.credit-limit-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.credit-limit-options-title {
  margin: 0;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: $brand-font-medium;
}

.credit-limit-option-card {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  padding: 0.9rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &.selected {
    border-color: #2874E9;
    background: #eff6ff;
    box-shadow: 0 0 0 3px rgba(40, 116, 233, 0.12);

    .credit-limit-option-radio {
      border-color: #2874E9;

      &::after {
        content: '';
        position: absolute;
        inset: 3px;
        border-radius: 50%;
        background: #2874E9;
      }
    }
  }
}

.credit-limit-option-radio {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 0.2rem;
  border: 2px solid #94a3b8;
  border-radius: 50%;
  position: relative;
}

.credit-limit-option-body {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}

.credit-limit-option-label {
  color: #0f172a;
  font-size: 0.925rem;
  font-weight: 600;
  font-family: $brand-font-medium;
  line-height: 1.35;
}

.credit-limit-option-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  color: #475569;
  font-size: 0.85rem;
  font-family: $brand-font-regular;

  strong {
    color: #0f172a;
    font-family: $brand-font-medium;
    font-variant-numeric: tabular-nums;
  }
}

.action-buttons {
  margin-top: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

@media (max-width: 640px) {
  .run-model-credit-limits-grid,
  .run-model-credit-limits-grid.has-wcr {
    grid-template-columns: 1fr;
  }
}
</style>
